import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Boxes, Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PmAccessGroups = () => {
  const [groups, setGroups] = useState([]);
  const [properties, setProperties] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {id?, name, property_ids, user_ids}
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: g }, { data: p }, { data: u }] = await Promise.all([
        axios.get(`${API}/pm/access-groups`, { withCredentials: true }),
        axios.get(`${API}/pm/company/properties`, { withCredentials: true }),
        axios.get(`${API}/pm/users`, { withCredentials: true }),
      ]);
      setGroups(g.items || []);
      setProperties(p.items || []);
      setUsers(u.items || []);
    } catch (e) {
      toast.error('Failed to load groups');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const startCreate = () => setEditing({ name: '', property_ids: [], user_ids: [] });
  const startEdit   = (g) => setEditing({ id: g.id, name: g.name, property_ids: [...(g.property_ids || [])], user_ids: [...(g.user_ids || [])] });

  const save = async () => {
    if (!editing.name?.trim()) {
      toast.error('Name required');
      return;
    }
    setBusy(true);
    try {
      if (editing.id) {
        await axios.put(`${API}/pm/access-groups/${editing.id}`, {
          name: editing.name, property_ids: editing.property_ids, user_ids: editing.user_ids,
        }, { withCredentials: true });
        toast.success('Group updated');
      } else {
        await axios.post(`${API}/pm/access-groups`, {
          name: editing.name, property_ids: editing.property_ids, user_ids: editing.user_ids,
        }, { withCredentials: true });
        toast.success('Group created');
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.code || 'Save failed');
    }
    setBusy(false);
  };

  const archiveGroup = async (g) => {
    if (!window.confirm(`Remove group "${g.name}"?`)) return;
    try {
      await axios.delete(`${API}/pm/access-groups/${g.id}`, { withCredentials: true });
      toast.success('Group removed');
      load();
    } catch (e) {
      toast.error('Remove failed');
    }
  };

  const togglePid = (pid) => setEditing((e) => ({ ...e, property_ids: e.property_ids.includes(pid) ? e.property_ids.filter((x) => x !== pid) : [...e.property_ids, pid] }));
  const toggleUid = (uid) => setEditing((e) => ({ ...e, user_ids: e.user_ids.includes(uid) ? e.user_ids.filter((x) => x !== uid) : [...e.user_ids, uid] }));

  return (
    <div className="p-6 space-y-6" data-testid="pm-access-groups">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-white flex items-center gap-2"><Boxes className="w-6 h-6 text-[#D4AF37]" /> Access Groups</h1>
          <p className="text-[#A3A3A3] text-sm mt-1">Group properties + users together. A Maintenance Tech sees every property in every group they belong to.</p>
        </div>
        <Button onClick={startCreate} data-testid="new-group-btn" className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#B8941F]"><Plus className="w-4 h-4 mr-1" /> New Group</Button>
      </div>

      <div className="bg-[#141414] border border-[#262626] rounded-md">
        {loading ? <div className="p-6 text-[#A3A3A3]">Loading…</div> : groups.length === 0 ? (
          <div className="p-12 text-center text-[#A3A3A3]" data-testid="no-groups">No access groups yet.</div>
        ) : (
          <ul className="divide-y divide-[#262626]">
            {groups.map((g) => (
              <li key={g.id} data-testid={`group-${g.id}`} className="p-4 flex items-center gap-4 hover:bg-[#1a1a1a]">
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium">{g.name}</p>
                  <div className="flex items-center gap-2 flex-wrap mt-1">
                    <Badge className="bg-[#262626] text-[#A3A3A3] border-[#404040]">{g.property_count} properties</Badge>
                    <Badge className="bg-[#262626] text-[#A3A3A3] border-[#404040]">{g.user_count} users</Badge>
                  </div>
                </div>
                <Button size="sm" variant="outline" className="border-[#404040] text-white" onClick={() => startEdit(g)} data-testid={`edit-group-${g.id}`}><Pencil className="w-3 h-3 mr-1" /> Edit</Button>
                <Button size="sm" variant="outline" className="border-red-500/50 text-red-300 hover:bg-red-500/10" onClick={() => archiveGroup(g)} data-testid={`remove-group-${g.id}`}><Trash2 className="w-3 h-3 mr-1" /> Remove</Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Edit group' : 'New group'}</DialogTitle>
            <DialogDescription className="text-[#A3A3A3]">Pick properties and users to include. Saving applies access immediately.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <Label>Group name</Label>
                <Input data-testid="group-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
              </div>

              <div>
                <Label>Properties ({editing.property_ids.length})</Label>
                <div className="grid grid-cols-2 gap-1 mt-1 max-h-48 overflow-y-auto p-2 bg-[#0A0A0A] border border-[#262626] rounded">
                  {properties.map((p) => (
                    <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-[#141414] p-1 rounded">
                      <input type="checkbox" data-testid={`pick-prop-${p.id}`} checked={editing.property_ids.includes(p.id)} onChange={() => togglePid(p.id)} />
                      <span className="truncate">{p.property_name || p.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label>Users ({editing.user_ids.length})</Label>
                <div className="grid grid-cols-2 gap-1 mt-1 max-h-48 overflow-y-auto p-2 bg-[#0A0A0A] border border-[#262626] rounded">
                  {users.map((u) => (
                    <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-[#141414] p-1 rounded">
                      <input type="checkbox" data-testid={`pick-user-${u.id}`} checked={editing.user_ids.includes(u.id)} onChange={() => toggleUid(u.id)} />
                      <span className="truncate">{u.name} <span className="text-[#A3A3A3]">({u.role})</span></span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="border-[#404040] text-white" onClick={() => setEditing(null)} disabled={busy}>Cancel</Button>
            <Button data-testid="save-group" className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#B8941F]" disabled={busy || !editing?.name} onClick={save}>{editing?.id ? 'Save' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PmAccessGroups;
